/**
 * Conversation hooks back the agent Overview "Recent conversations" widget, the
 * full conversation list, and the transcript drill-in.
 */

import type { SessionSummaryView } from "@lifter/hub-ui/browser-shared";
import {
  conversationAddress,
  type HubChatSendInput,
  hubQueryKeys,
} from "@lifter/hub-ui/browser-shared";
import {
  keepPreviousData,
  type QueryClient,
  useInfiniteQuery,
  useMutation,
  useQuery,
  useQueryClient,
} from "@tanstack/react-query";
import { type AgentQueryOptions, useHubApi } from "./context";

/** Page size for the archived-conversation recovery list. */
const ARCHIVED_PAGE_SIZE = 30;

function agentRouteConversationsKey(agentId: string) {
  return [...hubQueryKeys.conversations(agentId), "route-identities"] as const;
}

/** Accepted sends refresh the conversation lists and archived transcript reads. */
export function useHubChatSendMutation(agentId: string) {
  const api = useHubApi();
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (input: HubChatSendInput) => api.conversations.send(agentId, input),
    onSuccess: (result, input) => {
      if (!result.ok || result.data.ok === false) return;
      queryClient.invalidateQueries({ queryKey: hubQueryKeys.workspaceConversations() });
      // The agent's conversation lists too, not just the cross-agent rail.
      // The first send gives the explicitly created conversation a title, and
      // the thread resolves its summary independently of the list filters.
      // The default chat list is a separate cache entry because its server
      // filter differs. Exact invalidation refreshes both without sweeping the
      // per-session history and participant entries beneath their shared
      // prefix.
      queryClient.invalidateQueries({ queryKey: hubQueryKeys.conversations(agentId), exact: true });
      queryClient.invalidateQueries({ queryKey: agentRouteConversationsKey(agentId), exact: true });
      queryClient.invalidateQueries({
        queryKey: [...hubQueryKeys.conversations(agentId), "resolve"],
      });
      queryClient.invalidateQueries({
        queryKey: hubQueryKeys.conversationHistory(agentId, input.sessionId),
      });
    },
  });
}

export function useAgentConversationsQuery(
  agentId: string,
  options: AgentQueryOptions & { kind?: "all" } = {},
) {
  const api = useHubApi();
  return useQuery({
    queryKey:
      options.kind === "all"
        ? agentRouteConversationsKey(agentId)
        : hubQueryKeys.conversations(agentId),
    queryFn: () =>
      api.conversations.list(agentId, options.kind === "all" ? { kind: "all" } : undefined),
    enabled: options.enabled ?? true,
    ...(options.fresh ? { staleTime: 0, gcTime: 0 } : {}),
  });
}

/**
 * Set one conversation's approval mode. `recordSessionId` is the conversation's
 * gateway record id, which is the only namespace this route resolves — a
 * hub-chat channel id (what the Workspace route itself carries) addresses
 * nothing here and answers 404.
 *
 * The resolved summary owns this control. Update it optimistically so the
 * selected mode remains visible while saving; a refusal restores the summary.
 * Successful saves also refresh list consumers from the authoritative server.
 */
export function useConversationConfirmationModeMutation(agentId: string, recordSessionId: string) {
  const api = useHubApi();
  const queryClient = useQueryClient();
  const conversationsKey = agentRouteConversationsKey(agentId);
  return useMutation({
    mutationFn: (mode: "manual" | "bypass") =>
      api.conversations.setConfirmationMode(agentId, recordSessionId, mode),
    onMutate: async (mode) => {
      const resolutionKey = [...hubQueryKeys.conversations(agentId), "resolve"];
      await queryClient.cancelQueries({ queryKey: resolutionKey });
      const resolved = queryClient.getQueriesData<{ conversation: SessionSummaryView }>({
        queryKey: resolutionKey,
      });
      queryClient.setQueriesData<{ conversation: SessionSummaryView }>(
        { queryKey: resolutionKey },
        (data) =>
          data?.conversation.sessionId === recordSessionId
            ? { conversation: { ...data.conversation, confirmationMode: mode } }
            : data,
      );
      return resolved;
    },
    onError: (_error, _mode, context) => {
      for (const [key, data] of context ?? []) queryClient.setQueryData(key, data);
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: conversationsKey, exact: true });
      queryClient.invalidateQueries({
        queryKey: [...hubQueryKeys.conversations(agentId), "resolve"],
      });
      queryClient.invalidateQueries({
        queryKey: hubQueryKeys.conversations(agentId),
        exact: true,
      });
      queryClient.invalidateQueries({ queryKey: hubQueryKeys.workspaceConversations() });
    },
  });
}

export function useConversationParticipantsQuery(
  agentId: string,
  sessionId: string,
  options: AgentQueryOptions = {},
) {
  const api = useHubApi();
  return useQuery({
    queryKey: hubQueryKeys.conversationParticipants(agentId, sessionId),
    queryFn: () => api.conversations.participants.list(agentId, sessionId),
    enabled: (options.enabled ?? true) && Boolean(sessionId),
  });
}

export function useConversationAudienceQuery(agentId: string, options: AgentQueryOptions = {}) {
  const api = useHubApi();
  return useQuery({
    queryKey: hubQueryKeys.conversationAudience(agentId),
    queryFn: () => api.conversations.audience(agentId),
    enabled: options.enabled ?? true,
  });
}

function invalidateConversationMembership(
  queryClient: QueryClient,
  agentId: string,
  sessionId: string,
) {
  queryClient.invalidateQueries({
    queryKey: hubQueryKeys.conversationParticipants(agentId, sessionId),
  });
  queryClient.invalidateQueries({ queryKey: hubQueryKeys.conversations(agentId) });
  queryClient.invalidateQueries({ queryKey: hubQueryKeys.workspaceConversations() });
}

export function useInviteConversationParticipantMutation(agentId: string, sessionId: string) {
  const api = useHubApi();
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (userId: string) =>
      api.conversations.participants.invite(agentId, sessionId, userId),
    onSuccess: () => invalidateConversationMembership(queryClient, agentId, sessionId),
  });
}

export function useLeaveConversationMutation(agentId: string, sessionId: string) {
  const api = useHubApi();
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: () => api.conversations.participants.leave(agentId, sessionId),
    onSuccess: () => invalidateConversationMembership(queryClient, agentId, sessionId),
  });
}

/**
 * Every archived conversation the caller can reach, across their specialists.
 * Paged on the server's keyset cursor exactly like the live rail — a recovery
 * list that stopped at one page would strand the oldest conversations, which
 * are precisely the ones most likely to have been archived.
 */
export function useArchivedWorkspaceConversationsQuery(options: { enabled?: boolean } = {}) {
  const api = useHubApi();
  return useInfiniteQuery({
    queryKey: hubQueryKeys.archivedConversations(),
    queryFn: ({ pageParam }) =>
      api.workspaceConversations({
        archived: "only",
        limit: ARCHIVED_PAGE_SIZE,
        ...(pageParam ? { before: pageParam } : {}),
      }),
    initialPageParam: undefined as string | undefined,
    getNextPageParam: (lastPage) => (lastPage.hasMore ? lastPage.nextCursor : undefined),
    enabled: options.enabled ?? true,
  });
}

export interface ConversationArchiveInput {
  agentId: string;
  sessionId: string;
  archived: boolean;
  /** How the caller's list addresses the row; the rail's rows are channels. */
  address?: "record" | "channel";
}

/**
 * Archive or restore a conversation. Every conversation list is invalidated,
 * not just the one the action was taken from: archiving is conversation-wide,
 * so the row leaves (or rejoins) the cross-agent rail, that agent's own list,
 * and both recovery reads at once.
 */
export function useConversationArchiveMutation() {
  const api = useHubApi();
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (input: ConversationArchiveInput) =>
      api.conversations.setArchived(input.agentId, input.sessionId, {
        archived: input.archived,
        ...(input.address ? { address: input.address } : {}),
      }),
    onSuccess: (_result, input) => {
      queryClient.invalidateQueries({ queryKey: hubQueryKeys.workspaceConversations() });
      queryClient.invalidateQueries({ queryKey: hubQueryKeys.conversations(input.agentId) });
      queryClient.invalidateQueries({ queryKey: hubQueryKeys.archivedConversations() });
    },
  });
}

/** Opening a route only resolves an existing, participant-scoped conversation. */
export function useHubChatRouteHistoryQuery(
  agentId: string,
  routeSessionId: string,
  routeAddress?: "record" | "channel",
) {
  const api = useHubApi();
  const conversation = useQuery({
    queryKey: [...hubQueryKeys.conversations(agentId), "resolve", routeSessionId, routeAddress],
    queryFn: () => api.conversations.resolve(agentId, routeSessionId, routeAddress),
    staleTime: 0,
    retry: false,
  });
  const summary = conversation.data?.conversation;
  const address = summary ? conversationAddress(summary) : undefined;
  return {
    conversation,
    summary,
    conversationIdentityReady: conversation.isSuccess,
    identityUnavailable: address?.address === "record" || Boolean(summary?.archivedAt),
    liveSessionId: address?.sessionId ?? routeSessionId,
  };
}

/** Call only from an explicit New chat action; reuse the request ID on retry. */
export function useCreateConversationMutation(agentId: string) {
  const api = useHubApi();
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (requestId: string) => api.conversations.create(agentId, requestId),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: hubQueryKeys.conversations(agentId) });
      queryClient.invalidateQueries({ queryKey: hubQueryKeys.workspaceConversations() });
    },
  });
}

interface ConversationHistoryQueryOptions extends AgentQueryOptions {
  /** Identifier namespace at the route that initiated this read. */
  address?: "record" | "channel";
  // Reverse-pagination window. `before` walks the cursor backwards through
  // the transcript; both are part of the query key, so each window caches as
  // a distinct entry rather than refetching over a shared one.
  limit?: number;
  before?: string;
  // Live pages surface a failed history read immediately instead of delaying
  // their explicit error state behind retries.
  // Defaults to the query-client's normal retry behaviour when omitted.
  retry?: false | number;
}

export function useConversationHistoryQuery(
  agentId: string,
  sessionId: string,
  options: ConversationHistoryQueryOptions = {},
) {
  const api = useHubApi();
  const { enabled, limit, before, retry, address } = options;
  return useQuery({
    queryKey: [
      ...hubQueryKeys.conversationHistory(agentId, sessionId),
      address ?? "record",
      limit,
      before,
    ] as const,
    queryFn: () => api.conversations.history(agentId, sessionId, { limit, before, address }),
    ...(retry !== undefined ? { retry } : {}),
    // `fresh` re-reads on every mount. Keep the cached window as placeholder
    // data while it revalidates, so the transcript never flashes a skeleton over
    // rows that are already on screen. gcTime is deliberately left alone: paging
    // relies on prior windows staying in the cache.
    ...(options.fresh ? { staleTime: 0 } : {}),
    enabled: (enabled ?? true) && Boolean(sessionId),
    // The detail view pages older history by re-querying with a larger window
    // (a new query key); keep the prior window visible so growing the
    // transcript never flashes a skeleton over already-rendered messages.
    placeholderData: keepPreviousData,
  });
}
